import { FC, useEffect } from 'react'

import {
  IconStepBack,
  IconStepForwards,
  Button,
  ButtonGroup
} from '@sequencehq/core-components'
import { useAtomValue, useSetAtom } from 'jotai'

import { usePermissions } from 'lib/rbac'
import {
  isEditingDisabledAtom,
  canUndoAtom,
  canRedoAtom,
  undoAtom,
  redoAtom
} from 'Automations/automationEditor/atoms'

export const UndoRedo: FC = () => {
  const { can } = usePermissions()
  const isEditingDisabled = useAtomValue(isEditingDisabledAtom)
  const canUndo = useAtomValue(canUndoAtom)
  const canRedo = useAtomValue(canRedoAtom)
  const smartUndo = useSetAtom(undoAtom)
  const smartRedo = useSetAtom(redoAtom)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditingDisabled) return

      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return
      }

      const isMod = e.metaKey || e.ctrlKey
      const key = e.key.toLowerCase()
      if (isMod && key === 'z' && !e.shiftKey && canUndo) {
        e.preventDefault()
        smartUndo()
      }
      if (isMod && key === 'z' && e.shiftKey && canRedo) {
        e.preventDefault()
        smartRedo()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [smartUndo, smartRedo, isEditingDisabled, canUndo, canRedo])

  if (!can('org.watchtower:update')) return null

  return (
    <ButtonGroup>
      <Button
        variant="secondary"
        icon={<IconStepBack color="inherit" />}
        aria-label="Undo"
        data-testid="automations.editor.undo"
        disabled={isEditingDisabled || !canUndo}
        onClick={() => smartUndo()}
      />
      <Button
        variant="secondary"
        icon={<IconStepForwards color="inherit" />}
        aria-label="Redo"
        data-testid="automations.editor.redo"
        disabled={isEditingDisabled || !canRedo}
        onClick={() => smartRedo()}
      />
    </ButtonGroup>
  )
}
